import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit, getCountFromServer, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Project, Proposal } from '../../models/types';
import { ChevronRight, PlusCircle, Eye, Clock, MessageSquare, Users, Check, X, Calendar } from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import ChatModal from '../../components/Chat/ChatModal';
import Avatar from '../../components/UI/Avatar';

interface ProjectWithProposals extends Project {
  proposals: {
    count: number;
    providers: Array<{
      id: string;
      name: string;
      email: string;
      proposalId: string;
      status: 'pending' | 'accepted' | 'rejected';
      submittedAt: Date;
      companyLogo?: string;
      companyName?: string;
    }>;
  };
}

const RequesterDashboard = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [projects, setProjects] = useState<ProjectWithProposals[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [selectedChat, setSelectedChat] = useState<{
    projectId: string;
    providerId: string;
    providerName: string;
  } | null>(null);

  // Add status options
  const statusOptions = [
    { value: 'active', label: t('project.active') },
    { value: 'closed', label: t('project.closed') },
    { value: 'archived', label: t('project.archived') }
  ];
  
  useEffect(() => {
    if (!currentUser) return;
    
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const projectsRef = collection(db, 'projects');
        
        // First try with compound query
        try {
          const q = query(
            projectsRef,
            where('userId', '==', currentUser.uid),
            orderBy('createdAt', 'desc'),
            limit(50)
          );
          
          const querySnapshot = await getDocs(q);
          const projectList: ProjectWithProposals[] = [];
          
          // Fetch each project's proposals
          for (const projectDoc of querySnapshot.docs) {
            const projectData = projectDoc.data();
            
            // Get proposal count using aggregation
            const proposalsRef = collection(db, 'proposals');
            const proposalsQuery = query(
              proposalsRef,
              where('projectId', '==', projectDoc.id)
            );
            const proposalCount = await getCountFromServer(proposalsQuery);
            
            // Get provider details for each proposal
            const proposalsSnapshot = await getDocs(proposalsQuery);
            const providers = [];
            
            for (const proposalDoc of proposalsSnapshot.docs) {
              const proposalData = proposalDoc.data();
              const userDoc = await getDocs(
                query(
                  collection(db, 'users'),
                  where('uid', '==', proposalData.providerId)
                )
              );
              
              if (!userDoc.empty) {
                const userData = userDoc.docs[0].data();
                
                // Fetch provider profile for company info and logo
                let companyLogo = '';
                let companyName = '';
                try {
                  const providerProfileDoc = await getDoc(doc(db, 'providerProfiles', proposalData.providerId));
                  if (providerProfileDoc.exists()) {
                    const profileData = providerProfileDoc.data();
                    companyLogo = profileData.companyLogo || '';
                    companyName = profileData.companyName || userData.username;
                  }
                } catch (error) {
                  console.warn('Could not fetch provider profile:', error);
                }
                
                providers.push({
                  id: userData.uid,
                  name: userData.username,
                  email: userData.email,
                  proposalId: proposalDoc.id,
                  status: proposalData.status,
                  submittedAt: proposalData.createdAt.toDate(),
                  companyLogo,
                  companyName: companyName || userData.username
                });
              }
            }
            
            // Sort providers by submission date (most recent first)
            providers.sort((a, b) => b.submittedAt.getTime() - a.submittedAt.getTime());
            
            projectList.push({
              id: projectDoc.id,
              ...projectData,
              createdAt: projectData.createdAt.toDate(),
              updatedAt: projectData.updatedAt.toDate(),
              proposals: {
                count: proposalCount.data().count,
                providers
              }
            } as ProjectWithProposals);
          }
          
          setProjects(projectList);
        } catch (indexError) {
          console.warn('Index not found, falling back to simple query');
          const simpleQuery = query(
            projectsRef,
            where('userId', '==', currentUser.uid)
          );
          
          const querySnapshot = await getDocs(simpleQuery);
          const projectList: ProjectWithProposals[] = [];
          
          for (const projectDoc of querySnapshot.docs) {
            const projectData = projectDoc.data();
            
            // Get proposal count using aggregation
            const proposalsRef = collection(db, 'proposals');
            const proposalsQuery = query(
              proposalsRef,
              where('projectId', '==', projectDoc.id)
            );
            const proposalCount = await getCountFromServer(proposalsQuery);
            
            // Get provider details for each proposal
            const proposalsSnapshot = await getDocs(proposalsQuery);
            const providers = [];
            
            for (const proposalDoc of proposalsSnapshot.docs) {
              const proposalData = proposalDoc.data();
              const userDoc = await getDocs(
                query(
                  collection(db, 'users'),
                  where('uid', '==', proposalData.providerId)
                )
              );
              
              if (!userDoc.empty) {
                const userData = userDoc.docs[0].data();
                
                // Fetch provider profile for company info and logo
                let companyLogo = '';
                let companyName = '';
                try {
                  const providerProfileDoc = await getDoc(doc(db, 'providerProfiles', proposalData.providerId));
                  if (providerProfileDoc.exists()) {
                    const profileData = providerProfileDoc.data();
                    companyLogo = profileData.companyLogo || '';
                    companyName = profileData.companyName || userData.username;
                  }
                } catch (error) {
                  console.warn('Could not fetch provider profile:', error);
                }
                
                providers.push({
                  id: userData.uid,
                  name: userData.username,
                  email: userData.email,
                  proposalId: proposalDoc.id,
                  status: proposalData.status,
                  submittedAt: proposalData.createdAt.toDate(),
                  companyLogo,
                  companyName: companyName || userData.username
                });
              }
            }
            
            // Sort providers by submission date (most recent first)
            providers.sort((a, b) => b.submittedAt.getTime() - a.submittedAt.getTime());
            
            projectList.push({
              id: projectDoc.id,
              ...projectData,
              createdAt: projectData.createdAt.toDate(),
              updatedAt: projectData.updatedAt.toDate(),
              proposals: {
                count: proposalCount.data().count,
                providers
              }
            } as ProjectWithProposals);
          }
          
          // Sort manually
          projectList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
          setProjects(projectList);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error(t('error.loading'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, [currentUser, t]);

  const handleStatusChange = async (projectId: string, newStatus: string) => {
    if (!currentUser) return;

    try {
      setUpdatingStatus(projectId);
      
      // Update project status
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        status: newStatus,
        updatedAt: new Date()
      });

      // Update local state
      setProjects(projects.map(project => 
        project.id === projectId 
          ? { ...project, status: newStatus, updatedAt: new Date() }
          : project
      ));

      toast.success(t('success.updated'));
    } catch (error) {
      console.error('Error updating project status:', error);
      toast.error(t('error.updating'));
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleProposalAction = async (projectId: string, proposalId: string, action: 'accept' | 'reject') => {
    try {
      // Update proposal status
      const proposalRef = doc(db, 'proposals', proposalId);
      await updateDoc(proposalRef, {
        status: action === 'accept' ? 'accepted' : 'rejected',
        updatedAt: new Date()
      });

      // Update local state
      setProjects(projects.map(project => {
        if (project.id === projectId) {
          return {
            ...project,
            proposals: {
              ...project.proposals,
              providers: project.proposals.providers.map(provider => 
                provider.proposalId === proposalId
                  ? { ...provider, status: action === 'accept' ? 'accepted' : 'rejected' }
                  : provider
              )
            }
          };
        }
        return project;
      }));

      toast.success(t(`success.proposal${action === 'accept' ? 'Accepted' : 'Rejected'}`));
    } catch (error) {
      console.error('Error updating proposal:', error);
      toast.error(t('error.updating'));
    }
  };

  const openChat = (projectId: string, providerId: string, providerName: string) => {
    setSelectedChat({ projectId, providerId, providerName });
  };

  const closeChat = () => {
    setSelectedChat(null);
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-yellow-100 text-yellow-700';
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'closed':
        return 'bg-red-100 text-red-700';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  
  const getTimelineLabel = (timeline: string) => {
    switch (timeline) {
      case '1':
        return t('project.timeline1');
      case '2-3':
        return t('project.timeline2-3');
      case '3-6':
        return t('project.timeline3-6');
      case '6+':
        return t('project.timeline6+');
      case 'unknown':
        return t('project.timelineUnknown');
      default:
        return timeline;
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto container-padding section-padding">
      <div className="flex justify-between items-center mb-6">
        <h1 className="heading-md">{t('project.myProjects')}</h1>
        <Link
          to="/project/create"
          className="bg-orange-500 text-white px-4 py-2 rounded-md text-button hover:bg-orange-600 transition-colors flex items-center"
        >
          <PlusCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
          {t('project.newProject')}
        </Link>
      </div>
      
      {projects.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md card-padding text-center">
          <h2 className="heading-sm mb-4">{t('project.noProjects')}</h2>
          <p className="text-body text-gray-600 mb-6">
            {t('project.createFirst')}
          </p>
          <Link
            to="/project/create"
            className="bg-orange-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-button hover:bg-orange-600 transition-colors inline-flex items-center"
          >
            <PlusCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            {t('project.createFirstProject')}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div key={project.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="card-padding">
                <div className="flex justify-between items-start mb-3">
                  <h2 className="heading-xs">{project.name}</h2>
                  <select
                    value={project.status}
                    onChange={(e) => handleStatusChange(project.id, e.target.value)}
                    disabled={updatingStatus === project.id}
                    className={`px-2 py-1 rounded-full text-label ${getStatusColor(project.status)} border-0 cursor-pointer`}
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <p className="text-body-sm text-gray-600 mb-4 line-clamp-2">
                  {project.shortDescription}
                </p>
                
                <div className="flex justify-between items-center mb-4 text-caption">
                  <div className="flex items-center">
                    <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span>{getTimelineLabel(project.timeline)}</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span>{project.proposals.count} {t('project.proposals')}</span>
                  </div>
                </div>

                {project.proposals.providers.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-label text-gray-700 mb-2">{t('project.proposals')}:</h3>
                    <div className="spacing-xs">
                      {project.proposals.providers.map((provider) => (
                        <div
                          key={provider.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                        >
                          <div className="flex items-center space-x-3 flex-grow min-w-0">
                            {/* Company Logo */}
                            <Avatar
                              src={provider.companyLogo}
                              name={provider.companyName}
                              type="company"
                              size="sm"
                            />
                            
                            <div className="flex-grow min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-label text-gray-900 truncate">
                                  {provider.name}
                                </span>
                                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                  provider.status === 'accepted' 
                                    ? 'bg-green-100 text-green-800'
                                    : provider.status === 'rejected'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {t(`proposal.${provider.status}`)}
                                </span>
                              </div>
                              <div className="flex items-center text-caption">
                                <Calendar className="h-3 w-3 mr-1" />
                                <span>{t('dashboard.submitted')} {format(provider.submittedAt, 'MMM d, yyyy')}</span>
                              </div>
                              {provider.companyName && provider.companyName !== provider.name && (
                                <p className="text-caption truncate">{provider.companyName}</p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2 ml-3">
                            {provider.status === 'pending' ? (
                              <>
                                <button
                                  onClick={() => handleProposalAction(project.id, provider.proposalId, 'accept')}
                                  className="text-green-600 hover:text-green-700 transition-colors p-1"
                                  title={t('proposal.accept')}
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleProposalAction(project.id, provider.proposalId, 'reject')}
                                  className="text-red-600 hover:text-red-700 transition-colors p-1"
                                  title={t('proposal.reject')}
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </>
                            ) : provider.status === 'accepted' ? (
                              <button
                                onClick={() => openChat(project.id, provider.id, provider.name)}
                                className="text-primary-600 hover:text-primary-700 transition-colors p-1"
                                title={t('chat.openChat')}
                              >
                                <MessageSquare className="h-4 w-4" />
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <Link
                  to={`/project/${project.id}`}
                  className="flex justify-center items-center w-full mt-4 py-2 border border-primary-600 text-primary-600 rounded-md hover:bg-primary-50 transition-colors text-button-sm"
                >
                  {t('project.viewDetails')}
                  <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Chat Modal */}
      {selectedChat && (
        <ChatModal
          projectId={selectedChat.projectId}
          chatId={`${selectedChat.projectId}_${selectedChat.providerId}`}
          otherUserId={selectedChat.providerId}
          otherUserName={selectedChat.providerName}
          onClose={closeChat}
        />
      )}
    </div>
  );
};

export default RequesterDashboard;